import type { PaigeWatchedFolder } from './paige.global';

/** The small word for a watched folder's state, the same one Company, Devices shows. */
const stateWords: Record<PaigeWatchedFolder['state'], string> = {
  watching: 'Watching',
  waiting: 'Waiting',
  problem: 'Needs attention',
  off: 'Off',
};

export function folderStateWord(state: PaigeWatchedFolder['state']): string {
  return stateWords[state];
}

/** A moment as the sheet prints it: "Oct 3, 2026, 9:14 AM" in this browser's own clock. */
export function whenWords(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** "Quiet: not heard from since …" when the computer has gone quiet; null while it is heard from. */
export function quietLine(
  folder: Pick<PaigeWatchedFolder, 'quiet' | 'lastSeenAt'>,
  when: (iso: string) => string = whenWords,
): string | null {
  if (!folder.quiet) return null;
  return folder.lastSeenAt
    ? `Quiet: not heard from since ${when(folder.lastSeenAt)}.`
    : 'Quiet: Paige has not heard from this computer yet.';
}

/** The last file the folder sent and when, or that nothing has come through it yet. */
export function lastFileLine(
  folder: Pick<PaigeWatchedFolder, 'lastFileName' | 'lastFileAt'>,
  when: (iso: string) => string = whenWords,
): string {
  if (!folder.lastFileName || !folder.lastFileAt) return 'No file has come through it yet.';
  return `Last file ${folder.lastFileName}, ${when(folder.lastFileAt)}.`;
}

/** What the folder's files wait for when the job reads nothing now; null when it reads them. */
export function jobWaitingLine(folder: Pick<PaigeWatchedFolder, 'jobWaiting'>): string | null {
  if (folder.jobWaiting === 'setup')
    return 'Its files wait for this setup: they are kept and read once the setup is accepted.';
  if (folder.jobWaiting === 'off')
    return 'Its files wait for this job to be switched on: they are kept and read once it is.';
  return null;
}

/** "3 sent today", "1 sent today", "Nothing sent today". */
export function sentTodayWords(sent: number): string {
  if (sent === 0) return 'Nothing sent today';
  return `${sent} sent today`;
}
